import { useContext, useEffect, useRef, useState } from 'react';
import React from 'react';
import styles from './TableColumnSelector.module.css';
import {
  getVisibleLeftPinnedColumns,
  getVisibleRightPinnedColumns,
  TableColumn,
  TableContext,
} from './Table';
import Button from '../forms/Button/Button';
import IconColumns from '../icons/IconColumns';
import IconSelect from '../icons/IconSelect';
import IconDrag from '../icons/IconDrag';
import Checkbox from '../forms/Checkbox/Checkbox';
import Flyout from '../Flyout';

type DragState = {
  draggingColumnId: null|string,
  draggingRectStartedAt: null|DOMRect,
  mouseYDragOffset: number,
  mouseYLast: number,
  absDragDistance: number,
  storedColumns: { [key: string]: ColumnDragState};
  lastClosed: number,
};
type ColumnDragState = {
  element: HTMLElement;
  transform: string;
  order: number;
};


interface ColumnListItemProps<TData> {
  column: TableColumn<TData>;
  onChecked: () => void;
  onMyDrag: () => void;
  columnDragState: ColumnDragState | null;
  isDragging: boolean;
}

const ColumnListItem = React.forwardRef(function ColumnListItem<TData>(
  { column, onChecked, onMyDrag, columnDragState, isDragging}: ColumnListItemProps<TData>, ref: (instance: HTMLElement | null) => void
) {
  const style = {
    transform: columnDragState?.transform,
  };

  return (
    <div data-column-for-sorting={column.id} ref={ref} style={style} className={styles.listItem}>
      <button
        onPointerDownCapture={onMyDrag}
        className={styles.listDragHandle}
      >
        <div className={styles.iconDragContainer}>
          <IconDrag />
        </div>
      </button>
      <label>
        <Checkbox value={column.visible} onChange={() => onChecked()} />
        <div>{column.title}</div>
      </label>
    </div>
  );
});

interface ColummSelectorProps<TData> {
  columns: TableColumn<TData>[];
}

export function TableColumnSelector<TData>({ columns }: ColummSelectorProps<TData>) {
  const { tableState, setTableState } = useContext(TableContext);
  const [columnList, setColumnList] = useState<TableColumn<TData>[]>(
    tableState.columns.map(tsCol => {
      const matchCol = columns.find(column => column.id === tsCol.id);
      matchCol.visible = tsCol.visible; //Make sure we persist visiblity value from state profile.
      return matchCol;
    }).filter(col => !col.pinned)
  );

  const [showColumnList, setShowColumnList] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const NO_LAST = -1337420;
  const MIN_DRAG_DISTANCE = 15;
  const dragState = useRef<DragState>({
    draggingColumnId: null,
    draggingRectStartedAt: null,
    mouseYDragOffset: 0,
    mouseYLast: NO_LAST,
    lastClosed: 0,
    storedColumns: {},
  });

  // @NOTE(michaelp): Re-render parent table
  useEffect(() => {

    const cols = dragState.current.storedColumns;
    const allColumns = [
      ...getVisibleLeftPinnedColumns(tableState.columns),
      ...columnList.sort((_a,_b) => { const a = cols[_a.id] ?? 1000; const b = cols[_b.id] ?? 1000; if(a > b) return 1; else if(a < b) return -1; else return 0;}),
      ...getVisibleRightPinnedColumns(tableState.columns),
    ] as TableColumn<TData>[];

    const reOrderedCols = allColumns.map(col => {
      const foundTsCol = tableState.columns.find(tsCol => tsCol.id === col.id);

      return {
        ...foundTsCol,
        visible: col.visible,
      };
    });

    setTableState({
      ...tableState,
      columns: reOrderedCols,
    });
  }, [ columnList ]);

  useEffect(() => {
    const handleClickOutsideColumnList = (e: MouseEvent) => {
      if(performance.now() - dragState.lastClosed > 200 && !dragState.draggingColumnId) {
        const outsideButton = buttonRef.current && !buttonRef.current.contains(e.target as Node);
        const outsideFlyout = flyoutRef.current && !flyoutRef.current.contains(e.target as Node);
        if (outsideButton && outsideFlyout) {
          setShowColumnList(false);
        }
      }
    };

    if (window) {
      window.addEventListener('click', handleClickOutsideColumnList);
      return () => {
        window.removeEventListener('click', handleClickOutsideColumnList);
      };
    }
  }, []);

  function stopDragging(applyVisualChanges = true) {
    document.body.classList.remove("draggingColumnSelectors");
    const list = document.querySelector("[data-sorted-column-list=\"true\"]") as HTMLElement|null;
    if(list) {
      const children = Array.prototype.slice.call(list.children);
      // console.log(children.map((c,i) => { return c.getAttribute("data-column-for-sorting") +": " + c.getBoundingClientRect().top; }));
      if(applyVisualChanges) {
        children.sort((_a,_b) => {
          let a = _a.getBoundingClientRect();
          let b = _b.getBoundingClientRect();
          if(a.top > b.top) return 1;
          else if(a.top < b.top) return -1;
          return 0;
        });
      }
      for(let i = 0; i < children.length; ++i) {
        const child = children[i];
        let id = child.getAttribute("data-column-for-sorting");
        dragState.current.storedColumns[id] = {
          order: i,
          transform: "",
        };
        child.style.transform = "";
        child.style.transition = "";
      }
      if(applyVisualChanges) {
        setColumnList([...columnList]);
      }
    }
    dragState.current.absDragDistance = 0;
    dragState.current.mouseYDragOffset = 0;
    dragState.current.draggingColumnId = null;
  }


  useEffect(() => {
    const state = dragState.current;
    function updateDraggingLogic(e: MouseEvent) {
      const draggingId = state.draggingColumnId;
      if(draggingId != null) {
        if(state.mouseYLast != NO_LAST) {
          const delta = e.clientY - state.mouseYLast;
          state.mouseYDragOffset += delta;
          state.absDragDistance += Math.abs(delta);
        }
        const col = state.storedColumns[state.draggingColumnId];
        const element = document.querySelector(`[data-column-for-sorting="${draggingId}"]`);
        if(col && element) {
          element.style.transition = "";
          if(!state.draggingRectStartedAt) {
            state.draggingRectStartedAt = element.getBoundingClientRect();
          }
          const parent = element.parentNode;
          const siblings = Array.prototype.slice.call(parent.children);
          const at = siblings.indexOf(element);
          const elementRect = element.getBoundingClientRect();
          for(let i = 0; i < siblings.length; i++) {
            const test = siblings[i];
            if(test != element) {
              test.style.transition = "transform 100ms ease-out";
              const testRect = test.getBoundingClientRect();
              const id = test.getAttribute("data-column-for-sorting");
              const testState: ColumnDragState = state.storedColumns[id] ?? {order:1000};
              const swapPixelsSoonerThanFullCrossover = elementRect.height/8.0;
              if(testState.order < col.order) {
                if(e.clientY < testRect.top + testRect.height/2) {
                  testState.transform = "translateY(" + testRect.height + "px)";
                  test.style.transform = testState.transform;
                } else if(e.clientY > testRect.top + testRect.height/2) {
                  testState.transform = "";
                  test.style.transform = testState.transform;
                }
              } else if(testState.order > col.order) {
                if(e.clientY > testRect.top + testRect.height/2) {
                  testState.transform = "translateY(" + -testRect.height + "px)";
                  test.style.transform = testState.transform;
                } else if(e.clientY < testRect.top + testRect.height/2) {
                  testState.transform = "";
                  test.style.transform = testState.transform;
                }
              }
            }
          }
        }
        state.mouseYLast = e.clientY;
      }
    }
    function updateDraggingPosition() {
      const draggingId = state.draggingColumnId;
      if(draggingId != null) {
        const col = state.storedColumns[state.draggingColumnId];
        const element = document.querySelector(`[data-column-for-sorting="${draggingId}"]`);
        if(col && element) {
          const parentRect: DOMRect = element.parentNode.getBoundingClientRect();
          let y = state.mouseYDragOffset;
          if(state.draggingRectStartedAt) {
            const minRelY = parentRect.top - state.draggingRectStartedAt.top;
            const maxRelY = parentRect.bottom - state.draggingRectStartedAt.bottom;
            if(y < minRelY) y = minRelY;
            if(y > maxRelY) y = maxRelY;
          }
          col.transform = "translateY(" + y + "px)";
          element.style.transform = col.transform;
        }
      }
    }
    window.addEventListener("pointermove", function(e: MouseEvent) {
      const draggingId = state.draggingColumnId;
      if(draggingId != null) {
        updateDraggingLogic(e);
        updateDraggingPosition();
      }
    });
    window.addEventListener("pointerup", function(e: MouseEvent) {
      if(state.draggingColumnId) {
        if(state.absDragDistance >= MIN_DRAG_DISTANCE) {
          updateDraggingLogic(e);
        }
        stopDragging(state.absDragDistance >= MIN_DRAG_DISTANCE);
        dragState.lastClosed = performance.now();
      }
      state.absDragDistance = 0;
    });
  }, []);

  for(let i = 0; i < columnList.length; i++) {
    const c = columnList[i];
    if(!dragState.current.storedColumns[c.id]) {
      dragState.current.storedColumns[c.id] = { order: i, transform: ""};
    }
    if(!showColumnList) {
      dragState.current.storedColumns[c.id].transform = "";
    }
  }
  if(!showColumnList) {
    stopDragging(false);
  }

  return (
    <div className={styles.columnSelector} ref={buttonRef}>
      <Button type="secondary" onClick={() => setShowColumnList(!showColumnList)}>
        <IconColumns /> <span>Columns</span><IconSelect />
      </Button>
      {showColumnList ? (
        <Flyout className={styles.listWrapper} anchor='right' matchWidth={false} ref={flyoutRef}>
          <div className={styles.list} data-sorted-column-list="true">
            {columnList
              .sort((_a,_b)=>{
                const a = dragState.current.storedColumns[_a.id];
                const b = dragState.current.storedColumns[_b.id];
                if(!a && b) return -1;
                if(a && !b) return 1;
                if(!a && !b) return 0;
                if(a.order > b.order) return 1;
                else if(a.order < b.order) return -1;
                else return 0;
              }).map((column, index) => {
                const onMyDrag = (e) => {
                  dragState.current.draggingColumnId = column.id;
                  dragState.current.mouseYDragOffset = 0;
                  dragState.current.mouseYLast = NO_LAST;
                  dragState.current.absDragDistance = 0;
                  dragState.current.draggingRectStartedAt = null;
                  document.body.classList.add("draggingColumnSelectors");
                };
                const setRef = (e) => {
                  dragState.current.storedColumns[column.id].element = e; 
                };
                const isDragging = dragState.current.draggingColumnId === column.id;
                return (
                  <ColumnListItem
                    onMyDrag={onMyDrag}
                    columnDragState={dragState.current.storedColumns[column.id]}
                    ref={setRef}
                    key={column.id}
                    column={column}
                    onChecked={() => {
                      if(dragState.current.absDragDistance >= MIN_DRAG_DISTANCE) return;
                      columnList[index].visible = !columnList[index].visible;
                      setColumnList([...columnList]);
                    }}
                  />
                );
              })
            }
          </div>
        </Flyout>) : null}
    </div>

  );
}
